import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useFollowLinksUpload } from "@/hooks/admin/accounts/useFollowLinksUpload";
import { Account } from "@/types/strategy-types";
import { Loader2, AlertTriangle, CheckCircle, Info } from "lucide-react";

interface FollowLinksUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  account: Account;
}

interface ParsedLink {
  url: string;
  capacity: number;
}

const FollowLinksUploadDialog: React.FC<FollowLinksUploadDialogProps> = ({ 
  isOpen, 
  onClose,
  account 
}) => {
  const [linksText, setLinksText] = useState("");
  const [parsedLinks, setParsedLinks] = useState<ParsedLink[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const { uploadFollowLinks, isUploading, progress } = useFollowLinksUpload();
  const [refreshDispatched, setRefreshDispatched] = useState(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (!linksText.trim()) {
      setParsedLinks([]);
      setParseError(null);
      setShowPreview(false);
      return;
    }

    try {
      const links = linksText.split("\n")
        .filter(line => line.trim().length > 0)
        .map(line => {
          if (line.includes(",")) {
            const [url, capacityStr] = line.split(",").map(part => part.trim());
            const capacity = parseInt(capacityStr, 10);
            
            if (isNaN(capacity) || capacity <= 0) {
              throw new Error(`Invalid capacity: "${capacityStr}" - must be a positive number`);
            }
            
            return { url, capacity };
          } else {
            return { url: line.trim(), capacity: 1000 };
          }
        });
      
      setParsedLinks(links);
      setParseError(null);
      setShowPreview(links.length > 0);
    } catch (error) {
      setParsedLinks([]);
      setParseError(error instanceof Error ? error.message : "Invalid format");
      setShowPreview(false);
    }
  }, [linksText]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Submitting follow links for account:", account.id);
    
    if (parseError || parsedLinks.length === 0) {
      return;
    }
    
    console.log(`Parsed ${parsedLinks.length} links from input with capacity values`);
    
    const success = await uploadFollowLinks(account.id, parsedLinks);
    console.log("Upload result:", success ? "Success" : "Failed");
    
    if (success) {
      setLinksText("");
      setParsedLinks([]);
      setShowPreview(false);
      
      scheduleRefreshes();
      
      if (progress.failed === 0 && !progress.inProgress) {
        onClose();
      }
    }
  };

  const scheduleRefreshes = () => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    console.log("Dispatching immediate refresh-link-counts event");
    window.dispatchEvent(new CustomEvent('refresh-link-counts'));
    setRefreshDispatched(true);
    
    const scheduleNextRefresh = (delay: number, remainingRefreshes: number) => {
      if (remainingRefreshes <= 0) {
        console.log("Completed scheduled refreshes");
        return;
      }
      
      refreshTimeoutRef.current = setTimeout(() => {
        console.log(`Dispatching follow-up refresh-link-counts event (${remainingRefreshes} remaining)`);
        window.dispatchEvent(new CustomEvent('refresh-link-counts'));
        
        scheduleNextRefresh(delay * 1.5, remainingRefreshes - 1);
      }, delay);
    };
    
    scheduleNextRefresh(1000, 3);
  };

  React.useEffect(() => {
    if (!isOpen) {
      setRefreshDispatched(false);
      setParsedLinks([]);
      setShowPreview(false);
      setParseError(null);
      
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    }
  }, [isOpen]);
  
  React.useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  const linkCount = parsedLinks.length;
    
  const progressPercentage = progress.inProgress
    ? Math.round((progress.uploaded / progress.total) * 100)
    : 0;
    
  const isLargeUpload = linkCount > 200;
  
  const uploadComplete = progress.total > 0 && progress.uploaded + progress.failed >= progress.total;
  const hasFailures = progress.failed > 0;
  const uploadSuccessful = uploadComplete && !hasFailures;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Follow Links</DialogTitle>
          <DialogDescription>
            Add follow links for account {account.external_id}. Enter one link per line. 
            You can specify capacity by adding a comma followed by the capacity number: 
            <code className="bg-slate-100 px-1 py-0.5 rounded text-sm">https://example.com/link, 500</code>
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <Textarea
              placeholder="https://example.com/link1&#10;https://example.com/link2, 500&#10;https://example.com/link3, 200"
              value={linksText}
              onChange={(e) => setLinksText(e.target.value)}
              rows={10}
              className="resize-none"
              disabled={isUploading}
            />
            
            {parseError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Format Error</AlertTitle>
                <AlertDescription>{parseError}</AlertDescription>
              </Alert>
            )}
            
            {showPreview && !parseError && (
              <div className="text-sm text-muted-foreground">
                <div className="flex items-center gap-1 mb-2">
                  <Info className="h-4 w-4" />
                  <span>Preview: {linkCount} link{linkCount !== 1 ? 's' : ''} ready to upload</span>
                </div>
                <div className="max-h-36 overflow-y-auto border rounded p-2 bg-slate-50">
                  {parsedLinks.slice(0, 5).map((link, index) => (
                    <div key={index} className="text-xs mb-1">
                      <span className="truncate block">{link.url}</span>
                      <span className="text-slate-500">Capacity: {link.capacity}</span>
                    </div>
                  ))}
                  {parsedLinks.length > 5 && (
                    <div className="text-xs text-slate-500">
                      ...and {parsedLinks.length - 5} more links
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {isLargeUpload && !isUploading && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Large Upload</AlertTitle>
                <AlertDescription>
                  You are uploading a large number of links ({linkCount}). 
                  They will be processed in batches and may take some time to complete.
                </AlertDescription>
              </Alert>
            )}
            
            {progress.inProgress && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Uploading {progress.uploaded + progress.failed}/{progress.total}</span>
                  <span>{progressPercentage}%</span>
                </div>
                <Progress value={progressPercentage} className="h-2" />
                {progress.failed > 0 && (
                  <p className="text-sm text-destructive">
                    {progress.failed} links failed to upload
                  </p>
                )}
              </div>
            )}
            
            {uploadComplete && refreshDispatched && (
              <Alert variant={uploadSuccessful ? "default" : "destructive"}>
                {uploadSuccessful ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <AlertTriangle className="h-4 w-4" />
                )}
                <AlertTitle>{uploadSuccessful ? "Upload Complete" : "Upload Completed with Errors"}</AlertTitle>
                <AlertDescription>
                  {uploadSuccessful 
                    ? `Successfully uploaded all ${progress.uploaded} links. Link counts will update automatically.`
                    : `Uploaded ${progress.uploaded} links, but ${progress.failed} links failed.`
                  }
                </AlertDescription>
              </Alert>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose} 
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isUploading || parseError !== null || parsedLinks.length === 0}
              className="min-w-[120px]"
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                "Upload Links"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FollowLinksUploadDialog;
